import { useState } from "react";
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { PlaybookChange } from "../../services/lensService";
import { formatWhen } from "./lensUtils";

const ACTIONS: Record<string, string> = {
    "create-type": "Playbook created",
    "update-type": "Playbook updated",
    "add-provision": "Provision added",
    "edit-provision": "Provision edited",
    "approve-provision": "Provision approved",
    "return-provision-to-draft": "Provision returned to draft",
    "delete-provision": "Provision archived",
    "restore-provision": "Provision restored",
    "update-review-note": "Review note updated",
    "approve-all": "All drafts approved",
    "ai-draft-from-precedent": "Drafted from precedent by AI",
};

const COLLAPSED_COUNT = 10;

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginTop: theme.spacing(4) },
    entry: { padding: theme.spacing(2, 0), borderBottom: "1px solid #eee", "&:last-child": { borderBottom: 0 } },
    head: { display: "flex", alignItems: "baseline", gap: theme.spacing(2), flexWrap: "wrap" },
    who: { marginLeft: "auto", color: "#555" },
    muted: { color: "#777" },
}));

const describe = (value: unknown): string =>
    value !== null && typeof value === "object" ? Object.values(value).map(String).join(" / ") : String(value);

// Lists become a count with the values on hover, so an approve-all of sixty keys stays one line.
const DetailLine = ({ detail }: { detail: unknown }) => {
    let parsed = detail;
    if (typeof detail === "string") {
        try {
            parsed = JSON.parse(detail);
        } catch {
            return <Typography variant="caption">{detail}</Typography>;
        }
    }
    if (!parsed || typeof parsed !== "object") return null;

    const parts = Object.entries(parsed as Record<string, unknown>)
        .filter(([, value]) => value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))
        .map(([field, value]) =>
            Array.isArray(value) ? (
                <Tooltip key={field} title={value.map(describe).join(", ")}>
                    <span style={{ textDecoration: "underline dotted" }}>
                        {value.length} {field}
                    </span>
                </Tooltip>
            ) : (
                <span key={field}>
                    {field}: {describe(value)}
                </span>
            ),
        );
    if (parts.length === 0) return null;

    return (
        <Typography variant="caption" component="div" style={{ color: "#555" }}>
            {parts.map((part, i) => (
                <span key={i}>
                    {i > 0 && " · "}
                    {part}
                </span>
            ))}
        </Typography>
    );
};

const Changelog = ({ changes, isLoading }: { changes: PlaybookChange[]; isLoading: boolean }) => {
    const { classes } = useStyles();
    const [showAll, setShowAll] = useState(false);
    const shown = showAll ? changes : changes.slice(0, COLLAPSED_COUNT);

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>
                Recent changes
            </Typography>
            {isLoading ? (
                <CircularProgress size={24} />
            ) : changes.length === 0 ? (
                <Typography variant="body2" className={classes.muted}>
                    No changes recorded yet.
                </Typography>
            ) : (
                <>
                    {shown.map((change, i) => (
                        <Box key={`${change.changedOn}-${i}`} className={classes.entry}>
                            <Box className={classes.head}>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {ACTIONS[change.action] ?? change.action}
                                </Typography>
                                {change.provisionKey && (
                                    <Typography variant="caption" className={classes.muted}>
                                        {change.provisionKey}
                                    </Typography>
                                )}
                                <Typography variant="caption" className={classes.who}>
                                    {change.byName || change.byEmail || "Not recorded"}
                                </Typography>
                                <Typography variant="caption" className={classes.muted}>
                                    {formatWhen(change.changedOn)}
                                </Typography>
                            </Box>
                            {change.detail && <DetailLine detail={change.detail} />}
                        </Box>
                    ))}
                    {changes.length > COLLAPSED_COUNT && (
                        <Button size="small" onClick={() => setShowAll((s) => !s)}>
                            {showAll ? "Show fewer" : `Show all ${changes.length}`}
                        </Button>
                    )}
                </>
            )}
        </Paper>
    );
};

export default Changelog;
